import { useEffect, useRef, useState } from 'react'
import { useQueries, UseQueryResult, UseQueryOptions } from 'react-query'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import {
  MagentoStoreConfig,
  MagentoVisitureProductsPayload,
  PageNormalized,
  ProductData,
  VisitureProductsQueryParams
} from 'lib/types'
import { ProductAttributeList } from 'lib/core/data/common/productAttributeList/types'
import { CATALOG_PAGE } from 'lib/core/resources/constants'
import { ChangeFilterHandler, normalizeProduct } from 'lib/modules/catalog'
import { MagentoClient } from 'lib/core/services/magento'

interface Deps {
  currentPage: number
  loading: boolean
  pageInfo: PageNormalized | undefined
  handleFilterChange: ChangeFilterHandler
  queryParams: VisitureProductsQueryParams
  magentoClient: MagentoClient | undefined
  withInfiniteScroll: boolean | undefined
  products: ProductData[] | undefined
  storeConfig: MagentoStoreConfig | undefined
  productAttributeList: ProductAttributeList | undefined
}

export const useInfiniteScrollAddon = ({
  currentPage,
  loading,
  pageInfo,
  handleFilterChange,
  queryParams,
  magentoClient,
  withInfiniteScroll = false,
  products,
  productAttributeList,
  storeConfig
}: Deps) => {
  const hasNextPage = pageInfo ? pageInfo.currentPage < pageInfo.totalPages : false
  const infiniteScrollResponse = useRef<UseQueryResult[]>()

  const [infiniteScrollRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    disabled: !withInfiniteScroll,
    onLoadMore: () => {
      if (!pageInfo) {
        return
      }

      const nextPage = pageInfo.currentPage + 1
      handleFilterChange({
        requestVar: CATALOG_PAGE.PAGE,
        value: [nextPage.toString()],
        replace: withInfiniteScroll
      })
    }
  })

  /**
   * Variables
   */
  const [shouldLoadInitialData] = useState(currentPage > 1)
  const [initialDataLoaded, setInitialDataLoaded] = useState(false)
  const pagesToLoad = shouldLoadInitialData ? currentPage - 1 : 0

  /**
   * Queries
   */
  const queries = new Array(pagesToLoad).fill(true).map((x, i): UseQueryOptions => {
    const params = {
      ...queryParams,
      currentPage: i + 1
    }
    return {
      queryKey: ['useProductListPage', params],
      queryFn: () => magentoClient?.catalog.fetchProductListPage(params),
      enabled: Boolean(withInfiniteScroll && pagesToLoad)
    }
  })

  infiniteScrollResponse.current = useQueries(queries)
  const initialDataLoading = infiniteScrollResponse.current?.some(data => data.isLoading)

  /**
   * Initial State
   */
  const [infiniteScrollProducts, setInfiniteScrollProducts] = useState<ProductData[] | undefined>(() => {
    const preloadedProducts = getProductsFromQueries({
      productAttributeList,
      storeConfig,
      queries: infiniteScrollResponse.current
    })

    if (!preloadedProducts || !products || !withInfiniteScroll) {
      return
    }

    setInitialDataLoaded(true)

    return [
      ...preloadedProducts,
      ...products
    ]
  })

  /**
   * Append Products
   */
  useEffect(() => {
    if ((shouldLoadInitialData && !initialDataLoaded) || !withInfiniteScroll || !products) {
      return
    }

    setInfiniteScrollProducts((state) => {
      if (currentPage === 1) {
        return products
      }

      if (state?.find(el => el.sku === products?.[0]?.sku)) {
        return state
      }

      return [
        ...(state ?? []),
        ...(products ?? [])
      ]
    })
  }, [
    products,
    initialDataLoaded,
    shouldLoadInitialData,
    setInfiniteScrollProducts,
    withInfiniteScroll,
    currentPage
  ])

  /**
   * Initial Data
   */
  useEffect(() => {
    if (!withInfiniteScroll || !shouldLoadInitialData || initialDataLoading || infiniteScrollProducts) {
      return
    }

    const preloadedProducts = getProductsFromQueries({
      productAttributeList,
      storeConfig,
      queries: infiniteScrollResponse.current
    })

    if (preloadedProducts && products) {
      setInitialDataLoaded(true)
      setInfiniteScrollProducts([
        ...preloadedProducts,
        ...products
      ])
    }
  }, [
    products,
    productAttributeList,
    storeConfig,
    initialDataLoading,
    infiniteScrollProducts,
    setInitialDataLoaded,
    setInfiniteScrollProducts,
    withInfiniteScroll,
    shouldLoadInitialData
  ])

  return {
    infiniteScrollHasNextPage: hasNextPage,
    infiniteScrollLoading: initialDataLoading,
    infiniteScrollRef,
    infiniteScrollProducts
  }
}

interface GetProductsFromQueriesDeps {
  productAttributeList?: ProductAttributeList
  storeConfig?: MagentoStoreConfig
  queries: UseQueryResult[] | undefined
}

const getProductsFromQueries = ({ productAttributeList, storeConfig, queries }: GetProductsFromQueriesDeps) => {
  if (!queries) {
    return
  }

  const products: ProductData[] = []

  queries?.forEach(res => {
    const data = (res.data as any)?.data?.data as MagentoVisitureProductsPayload | undefined
    data?.visitureProducts?.items.forEach(product => {
      products.push(normalizeProduct({
        product,
        productAttributeList: productAttributeList,
        storeConfig
      }))
    })
  })

  return products
}
